import type { ReactNode } from 'react';
import type { Category } from '@services/knowledge.service';
import { VisibilityChip } from './ui';

// 12e — the category tree.
//
// Phase 29 §1 cuts one line and adds one. The designed
// `2 OPEN GAPS IN THIS CATEGORY` needs the gap data; in its place, an EMPTY
// category is named: `NO ARTICLES YET`, derived from `articleCount === 0`.
// That is the same editorial signal — here is where the holes are — from a
// source that already exists. The person reorganising the KB is exactly the
// person who should see it.

const MONO = 'font-mono';

const W = { articles: 96, visibility: 110, actions: 70 } as const;

function visibilityOf(cat: Category): 'public' | 'internal' | 'inactive' {
  if (cat.isActive === false) return 'inactive';
  return cat.isPublic ? 'public' : 'internal';
}

/**
 * The meta line under the name.
 *
 * Order matters: an inactive category's own state outranks everything, then
 * the emptiness that makes it a commissioning brief, then the shape of the
 * tree. Only one clause is appended after the slug, so the line never becomes
 * a list nobody reads.
 */
function metaFor(cat: Category): { text: string; warn: boolean } {
  const slug = `/${cat.slug}`;
  if (cat.isActive === false) {
    return { text: `${slug} · INACTIVE, HIDDEN EVERYWHERE`, warn: false };
  }
  if ((cat.articleCount ?? 0) === 0) {
    return { text: `${slug} · NO ARTICLES YET`, warn: true };
  }
  const children = cat.children?.length ?? 0;
  if (children > 0) {
    return {
      text: `${slug} · ${children} SUBCATEGOR${children === 1 ? 'Y' : 'IES'}`,
      warn: false,
    };
  }
  if (!cat.isPublic) return { text: `${slug} · AGENTS ONLY`, warn: false };
  return { text: slug, warn: false };
}

export function CategoryTreeHead() {
  return (
    <div
      role="row"
      className={`${MONO} flex items-center bg-[var(--co-sunken)] px-[24px] py-[9px] text-[9.5px] font-semibold tracking-[.2em] text-[var(--co-ink-4)]`}
    >
      <div role="columnheader" className="min-w-0 flex-1">
        CATEGORY
      </div>
      <div role="columnheader" className="flex-none text-right" style={{ width: W.articles }}>
        ARTICLES
      </div>
      <div role="columnheader" className="flex-none text-right" style={{ width: W.visibility }}>
        VISIBILITY
      </div>
      <div role="columnheader" className="flex-none" style={{ width: W.actions }} />
    </div>
  );
}

export function CategoryRow({
  cat,
  level,
  expanded,
  hasChildren,
  dragging,
  dragOver,
  icon,
  onToggleExpand,
  onSelect,
  onEdit,
  onDelete,
  dragHandlers,
}: {
  cat: Category;
  level: number;
  expanded: boolean;
  hasChildren: boolean;
  dragging: boolean;
  dragOver: boolean;
  /** The category's chosen glyph, resolved by the caller. */
  icon: ReactNode;
  onToggleExpand: () => void;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
  dragHandlers: React.HTMLAttributes<HTMLDivElement> & { draggable?: boolean };
}) {
  const isParent = level === 0;
  const inactive = cat.isActive === false;
  const meta = metaFor(cat);

  return (
    <div
      role="row"
      {...dragHandlers}
      className="flex cursor-grab items-center border-b border-[var(--co-line-subtle)]"
      style={{
        paddingTop: isParent ? 12 : 11,
        paddingBottom: isParent ? 12 : 11,
        paddingLeft: 24 + level * 22,
        paddingRight: 24,
        opacity: dragging ? 0.5 : 1,
        // A drop target is marked by a rule, not a tint — the row underneath
        // still has to be readable while you hover it.
        boxShadow: dragOver ? 'inset 0 -2px 0 0 var(--co-ink)' : undefined,
      }}
    >
      <span
        className={`${MONO} mr-2 flex-none text-[11px] text-[var(--co-line-strong)]`}
        aria-hidden="true"
        title="Drag to reorder within this level. To move it under a different parent, edit the category."
      >
        ⠿
      </span>

      {hasChildren ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand();
          }}
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse ${cat.name}` : `Expand ${cat.name}`}
          className="mr-1 flex-none text-[10px] text-[var(--co-ink-3)]"
        >
          {expanded ? '▾' : '▸'}
        </button>
      ) : (
        <>
          <span className="mr-1 w-[10px] flex-none" />
          {/* The connector rule is what makes a child read as a child without
              indenting so far that the name column loses its left edge. */}
          {!isParent && (
            <span
              className="mr-2 h-px w-[22px] flex-none bg-[var(--co-line-strong)]"
              aria-hidden="true"
            />
          )}
        </>
      )}

      {/* Solid border for a parent, lighter for a child, dashed when inactive —
          the icon cell carries the tree's structure and the category's state
          without a second chip. */}
      <span
        className="mr-3 flex h-6 w-6 flex-none items-center justify-center border"
        style={{
          borderColor: isParent ? 'var(--co-brass-line)' : 'var(--co-line-strong)',
          borderStyle: inactive ? 'dashed' : 'solid',
          color: inactive ? 'var(--co-ink-4)' : 'var(--co-ink-2)',
        }}
      >
        {icon}
      </span>

      <button
        type="button"
        onClick={onSelect}
        className="min-w-0 flex-1 text-left"
      >
        <div
          className="truncate text-[var(--co-ink)]"
          style={{ fontSize: isParent ? 13.5 : 13, fontWeight: isParent ? 600 : 400 }}
        >
          {cat.name}
        </div>
        <div
          className={`${MONO} mt-[3px] truncate text-[10.5px] tracking-[.08em]`}
          style={{ color: meta.warn ? 'var(--co-terracotta)' : 'var(--co-ink-4)' }}
        >
          {meta.text}
        </div>
      </button>

      <div
        className={`${MONO} flex-none text-right text-[12px] tabular-nums`}
        style={{
          width: W.articles,
          color: (cat.articleCount ?? 0) === 0 ? 'var(--co-line-strong)' : 'var(--co-ink-2)',
        }}
      >
        {cat.articleCount ?? '—'}
      </div>

      <div className="flex flex-none justify-end" style={{ width: W.visibility }}>
        <VisibilityChip visibility={visibilityOf(cat)} />
      </div>

      <div className="flex flex-none items-center justify-end gap-1" style={{ width: W.actions }}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          aria-label={`Edit ${cat.name}`}
          className="flex h-6 w-6 items-center justify-center text-[12px] text-[var(--co-ink-3)] hover:text-[var(--co-ink)]"
        >
          ✎
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label={`Delete ${cat.name}`}
          className="flex h-6 w-6 items-center justify-center text-[13px]"
          style={{ color: 'var(--co-burgundy)' }}
        >
          ⋯
        </button>
      </div>
    </div>
  );
}
